<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { isCoarsePointerDevice } from '@/platform/viewport/layoutViewport'

const props = withDefaults(defineProps<{
  modelValue: number
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  readonly?: boolean
  placeholder?: string
  dragStepPx?: number
  commitOnBlur?: boolean
}>(), {
  min: Number.NEGATIVE_INFINITY,
  max: Number.POSITIVE_INFINITY,
  step: 1,
  dragStepPx: 4,
  commitOnBlur: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: number]
}>()

const inputRef = ref<HTMLInputElement | null>(null)
const inputText = ref(String(props.modelValue ?? 0))
const dragging = ref(false)
const pointerId = ref<number | null>(null)
const dragStartX = ref(0)
const dragStartY = ref(0)
const dragStartValue = ref(0)
const dragMoved = ref(false)
const isCoarsePointer = ref(false)
const isTouchTextEditing = ref(false)
let coarsePointerQuery: MediaQueryList | null = null
const TOUCH_DRAG_THRESHOLD = 10

const numericValue = computed(() => Number(props.modelValue))
const safeNumericValue = computed(() => (Number.isFinite(numericValue.value) ? numericValue.value : 0))
const canDecrease = computed(() => !props.disabled && !props.readonly && numericValue.value > props.min)
const canIncrease = computed(() => !props.disabled && !props.readonly && numericValue.value < props.max)
const inputReadonly = computed(() => props.readonly || (isCoarsePointer.value && !isTouchTextEditing.value))

watch(
  () => props.modelValue,
  (value) => {
    if (!dragging.value) {
      inputText.value = String(value ?? 0)
    }
  },
)

function normalizeValue(value: number) {
  const safeStep = Math.abs(props.step) > 0 ? Math.abs(props.step) : 1
  if (!Number.isFinite(value)) value = 0
  const snapped = Math.round(value / safeStep) * safeStep
  const clamped = Math.min(props.max, Math.max(props.min, snapped))
  const precision = safeStep.toString().includes('.') ? safeStep.toString().split('.')[1]?.length ?? 0 : 0
  return precision > 0 ? Number(clamped.toFixed(precision)) : clamped
}

function commitValue(value: number) {
  const next = normalizeValue(value)
  inputText.value = String(next)
  emit('update:modelValue', next)
}

function adjustBy(deltaSteps: number) {
  if (props.disabled || props.readonly) return
  commitValue(safeNumericValue.value + deltaSteps * props.step)
}

function onInput(rawValue: string) {
  inputText.value = rawValue
  if (props.commitOnBlur) return
  if (rawValue.trim() === '' || rawValue === '-' || rawValue === '.' || rawValue === '-.') return
  const parsed = Number(rawValue)
  if (!Number.isFinite(parsed)) return
  commitValue(parsed)
}

function onEnter() {
  if (inputText.value.trim() === '' || !Number.isFinite(Number(inputText.value))) return
  commitValue(Number(inputText.value))
  inputRef.value?.blur()
}

function onBlur() {
  if (inputText.value.trim() === '' || !Number.isFinite(Number(inputText.value))) {
    inputText.value = String(props.modelValue ?? 0)
    isTouchTextEditing.value = false
    return
  }
  commitValue(Number(inputText.value))
  isTouchTextEditing.value = false
}

function stopDrag() {
  if (pointerId.value !== null && inputRef.value?.hasPointerCapture(pointerId.value)) {
    inputRef.value.releasePointerCapture(pointerId.value)
  }
  pointerId.value = null
  dragging.value = false
  dragStartX.value = 0
  dragStartY.value = 0
  dragStartValue.value = 0
  window.getSelection()?.removeAllRanges()
}

function onPointerDown(event: PointerEvent) {
  if (props.disabled || props.readonly) return
  pointerId.value = event.pointerId
  dragging.value = !(event.pointerType === 'touch' || isCoarsePointer.value)
  dragMoved.value = false
  dragStartX.value = event.clientX
  dragStartY.value = event.clientY
  dragStartValue.value = safeNumericValue.value
  inputRef.value?.setPointerCapture(event.pointerId)
}

function onPointerMove(event: PointerEvent) {
  if (pointerId.value !== event.pointerId) return
  const deltaX = event.clientX - dragStartX.value
  const deltaY = event.clientY - dragStartY.value
  if (!dragging.value) {
    const absX = Math.abs(deltaX)
    const absY = Math.abs(deltaY)
    if (absY > TOUCH_DRAG_THRESHOLD && absY > absX) {
      stopDrag()
      return
    }
    if (absX < TOUCH_DRAG_THRESHOLD || absX <= absY) return
    dragging.value = true
    isTouchTextEditing.value = false
  }
  event.preventDefault()
  const safePx = Math.max(4, props.dragStepPx)
  const deltaSteps = Math.trunc(deltaX / safePx)
  if (deltaSteps !== 0) {
    dragMoved.value = true
    commitValue(dragStartValue.value + deltaSteps * props.step)
  }
}

function onPointerUp(event: PointerEvent) {
  if (pointerId.value !== event.pointerId) return
  const shouldEditTouch = event.pointerType === 'touch' || isCoarsePointer.value
  const shouldFocus = !dragMoved.value
  stopDrag()
  if (shouldFocus && shouldEditTouch) {
    enableTouchTextEditing()
    return
  }
  if (shouldFocus) {
    inputRef.value?.focus()
    inputRef.value?.select()
  }
}

function onFocus() {
  if (isCoarsePointer.value && !isTouchTextEditing.value) {
    inputRef.value?.blur()
  }
}

function enableTouchTextEditing() {
  if (props.disabled || props.readonly) return
  isTouchTextEditing.value = true
  requestAnimationFrame(() => {
    inputRef.value?.focus()
    inputRef.value?.select()
  })
}

function syncCoarsePointer() {
  isCoarsePointer.value = isCoarsePointerDevice()
  if (!isCoarsePointer.value) isTouchTextEditing.value = false
}

onMounted(() => {
  syncCoarsePointer()
  coarsePointerQuery = window.matchMedia('(hover: none), (pointer: coarse)')
  coarsePointerQuery.addEventListener('change', syncCoarsePointer)
  window.addEventListener('resize', syncCoarsePointer)
})

onBeforeUnmount(() => {
  coarsePointerQuery?.removeEventListener('change', syncCoarsePointer)
  window.removeEventListener('resize', syncCoarsePointer)
  coarsePointerQuery = null
})
</script>

<template>
  <div
    class="number-stepper-field"
    :class="{
      'number-stepper-field--dragging': dragging,
      'number-stepper-field--disabled': disabled,
    }"
  >
    <button
      type="button"
      class="number-stepper-field__button"
      :disabled="!canDecrease"
      @click="adjustBy(-1)"
    >
      -
    </button>
    <input
      ref="inputRef"
      class="number-stepper-field__input"
      type="text"
      inputmode="decimal"
      :value="inputText"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="inputReadonly"
      @input="onInput(($event.target as HTMLInputElement).value)"
      @keydown.enter.prevent="onEnter"
      @blur="onBlur"
      @focus="onFocus"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="stopDrag"
    />
    <button
      type="button"
      class="number-stepper-field__button"
      :disabled="!canIncrease"
      @click="adjustBy(1)"
    >
      +
    </button>
  </div>
</template>

<style scoped>
.number-stepper-field {
  --number-stepper-button-width: 28px;
  --number-stepper-input-min-width: 2.67ch;
  --number-stepper-min-width: calc(var(--number-stepper-button-width) * 2 + var(--number-stepper-input-min-width) + 16px);
  display: grid;
  grid-template-columns: var(--number-stepper-button-width) minmax(0, 1fr) var(--number-stepper-button-width);
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.number-stepper-field.cell-input,
.number-stepper-field.variable-input--sm,
.number-stepper-field.variable-input--md {
  padding: 0;
  border: 0;
  background: transparent;
  font-size: inherit;
}

.number-stepper-field.cell-input.sm,
.number-stepper-field.variable-input--sm {
  width: 100%;
  min-width: var(--number-stepper-sm-min-width, var(--number-stepper-min-width));
  max-width: 100%;
  flex: 1 1 0;
}

.number-stepper-field.cell-input {
  max-width: 100%;
}

.number-stepper-field.variable-input--md {
  width: 100%;
  min-width: var(--number-stepper-md-min-width, var(--number-stepper-min-width));
  max-width: 100%;
  flex: 1 1 0;
}

.number-stepper-field__button,
.number-stepper-field__input {
  height: 28px;
  border: 1px solid var(--theme-bg-secondary);
  background: var(--theme-bg-surface);
  color: var(--theme-text-heading);
  font-size: 13px;
  box-sizing: border-box;
}

.number-stepper-field__button {
  width: var(--number-stepper-button-width);
  min-width: var(--number-stepper-button-width);
  padding: 0;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.number-stepper-field__button:disabled {
  cursor: not-allowed;
  color: var(--theme-text-secondary);
}

.number-stepper-field__input {
  min-width: 0;
  padding: 4px 6px;
  text-align: center;
  border-left: 0;
  border-right: 0;
  touch-action: manipulation;
  user-select: none;
  cursor: text;
}

.number-stepper-field__input:focus {
  outline: none;
  border-color: var(--theme-accent);
}

.number-stepper-field--dragging .number-stepper-field__input {
  border-color: var(--theme-accent);
}

@media (hover: hover) and (pointer: fine) {
  .number-stepper-field__input {
    touch-action: none;
    cursor: ew-resize;
  }
}
</style>
